/**
 * MODULE: domain/sales-intelligence/frontend/components/ResearchStatusPanel.tsx
 * PURPOSE: Real-time LLM progress display during research (terminal/console style)
 *
 * DEPENDENCIES:
 *   - react: useEffect, useRef
 *
 * PUBLIC API:
 *   - ResearchStatusPanel: Progress panel with thoughts/deep-thinking output
 *
 * SIDE EFFECTS:
 *   - None (pure presentation)
 *
 * AGENT SAFETY: SAFE
 *
 * Node progress mapping: web_search → 25%, llm_step → 40%, entity_extraction → 60%,
 * fit_step / product_matching mid-pipeline, insight_generation → 80%, storage → 100%
 *
 * Recent: 2026-02-07 - Added for SalesIntelligenceFormModal and ChatLoadingSpinner progress display
 *
 * See: /domain/sales-intelligence/README.md
 */

import React, { useEffect, useRef } from 'react';

export interface ResearchStatusPanelProps {
  isLoading: boolean;
  progress: number;
  currentStep: string | null;
  /** Accumulated SSE text chunks (thoughts/deep-thinking) */
  output: string;
  error: string | null;
}

export function ResearchStatusPanel({
  isLoading,
  progress,
  currentStep,
  output,
  error,
}: ResearchStatusPanelProps) {
  const outputRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [output]);

  return (
    <div className="rounded-lg border border-gray-700 bg-gray-900 overflow-hidden">
      {/* Progress bar */}
      <div className="px-4 py-3 border-b border-gray-700">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-green-400">
            {currentStep || 'Initializing...'}
          </span>
          <span className="text-xs text-gray-500">{progress}%</span>
        </div>
        <div className="w-full bg-gray-800 rounded-full h-1.5">
          <div
            className="bg-green-500 h-1.5 rounded-full transition-all duration-300"
            style={{ width: `${Math.min(100, progress)}%` }}
          />
        </div>
      </div>

      {/* Scrollable output (terminal-style) */}
      <div
        ref={outputRef}
        className="h-32 overflow-y-auto p-4 font-mono text-xs text-green-400 leading-relaxed"
      >
        {output ? (
          <pre className="whitespace-pre-wrap break-words m-0">{output}</pre>
        ) : isLoading && !error ? (
          <span className="text-gray-500">Waiting for analysis...</span>
        ) : null}
      </div>

      {/* Error state */}
      {error && (
        <div className="px-4 py-2 border-t border-gray-700 bg-red-900/20 text-red-400 text-xs">
          {error}
        </div>
      )}
    </div>
  );
}
